import { useState, useCallback } from 'react'
import { generateCSV, downloadCSV } from '../utils/csv-export'
import { Alert } from 'react-native'
import * as Sentry from '@sentry/react-native'

interface UseExportDataProps<TItem> {
  fetchBatch: (
    offset: number,
    limit: number
  ) => Promise<{ items: TItem[]; total: number } | null>
  getHeaders: () => string[]
  mapRow: (item: TItem, index: number) => string[]
  filename: string
  limit?: number
}

export function useExportData<TItem>({
  fetchBatch,
  getHeaders,
  mapRow,
  filename,
  limit = 200,
}: UseExportDataProps<TItem>) {
  const [isExporting, setIsExporting] = useState(false)
  const [progress, setProgress] = useState(0)

  const exportData = useCallback(async () => {
    setIsExporting(true)
    setProgress(0.1)

    try {
      const allItems: TItem[] = []
      let offset = 0
      let hasMore = true

      // Fetch all items in batches
      while (hasMore) {
        const result = await fetchBatch(offset, limit)

        if (!result) {
          throw new Error('No data to export')
        }

        const { items, total } = result
        allItems.push(...items)

        // Update progress based on total
        const fetchedCount = offset + items.length
        setProgress(0.1 + (fetchedCount / total) * 0.7)

        // Check if there are more records
        hasMore = items.length === limit && fetchedCount < total
        offset += limit
      }

      setProgress(0.8)

      // Generate CSV
      const headers = getHeaders()
      const rows = allItems.map((item, index) => mapRow(item, index))

      const csv = generateCSV(headers, rows)
      setProgress(0.9)

      // Download CSV
      const timestamp = new Date().toISOString().split('T')[0]
      const csvFilename = `${filename}-${timestamp}.csv`
      await downloadCSV(csv, csvFilename)

      setProgress(1)
    } catch (error) {
      console.error('Export error:', error)
      Sentry.captureException(error)
      Alert.alert('Error', (error as Error).message)
    } finally {
      setIsExporting(false)
      setProgress(0)
    }
  }, [fetchBatch, getHeaders, mapRow, filename, limit])

  return {
    exportData,
    isExporting,
    progress,
  }
}
